// Reset user menu expanded state when mobile menu closes
import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import LogoutIcon from "@mui/icons-material/LogoutOutlined";
import GreenButton from "../GreenButton/GreenButton";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import EditIcon from "@mui/icons-material/BorderColorOutlined";
import LoyaltyIcon from "@mui/icons-material/Loyalty";
import AddressIcon from "@mui/icons-material/PinDropOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import UserIcon from "./../../assets/images/user.png";
import Ordersicon from "@mui/icons-material/RequestQuoteOutlined";
import NotificaionsIcon from "@mui/icons-material/NotificationsActiveOutlined";

const MobileMenu = ({
  isOpen,
  toggleMenu,
  activeItem,
  setActiveItem,
  isMobile,
  currentLanguage,
  changeLanguage,
  onLoginClick,
  onLogout,
}) => {
  const menuRef = useRef(null);
  const { user, isAuthenticated, logout, singleBooking } = useAuth();
  const { t } = useLanguage();
  const [userMenuExpanded, setUserMenuExpanded] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setUserMenuExpanded(false);
    }
  }, [isOpen]);
  // Add animation delay for menu items
  const getMenuItemDelay = (index) => {
    return { animationDelay: `${0.1 + index * 0.05}s` };
  };

  const handleNavClick = (item) => {
    setActiveItem(item);
    toggleMenu();
  };

  const handleLogout = async () => {
    // If onLogout prop is provided, use it; otherwise use the logout from context
    if (onLogout) {
      await onLogout();
    } else {
      await logout();
    }
    toggleMenu();
  };

  const handleUserMenuItemClick = (callback) => {
    // Close both the user dropdown menu and the mobile menu
    setUserMenuExpanded(false);
    toggleMenu();

    // Then execute the callback if provided
    if (callback && typeof callback === "function") {
      callback();
    }
  };

  const toggleUserMenu = () => {
    setUserMenuExpanded(!userMenuExpanded);
  };

  return (
    <div className={`navbar__mobile-menu ${isOpen ? "open" : ""}`}>
      <div className="navbar__mobile-menu-content">
        <div className="navbar__mobile-header">
          {/* Language selector next to close icon */}
          <div className="mobile-language-selector">
            <div
              className={`language-option ${
                currentLanguage === "ar" ? "active" : ""
              }`}
              onClick={() => changeLanguage("ar")}
            >
              <img
                src="https://flagcdn.com/sa.svg"
                alt="KSA Flag"
                className="flag-icon"
              />
            </div>
            <div
              className={`language-option ${
                currentLanguage === "en" ? "active" : ""
              }`}
              onClick={() => changeLanguage("en")}
            >
              <img
                src="https://flagcdn.com/gb.svg"
                alt="UK Flag"
                className="flag-icon"
              />
            </div>
          </div>

          <button className="navbar__mobile-close" onClick={toggleMenu}>
            <CloseIcon className="icon" />
          </button>
        </div>

        {/* User profile section if authenticated */}
        {isAuthenticated && user && (
          <div className="mobile-user-profile" style={getMenuItemDelay(0)}>
            <div className="user-profile-header" onClick={toggleUserMenu}>
              <div className="user-avatar-container">
                {user.profileImage ? (
                  <img
                    src={user.profileImage}
                    alt={user.name}
                    className="user-avatar"
                  />
                ) : (
                  <img src={UserIcon} alt="User" className="user-avatar" />
                )}
              </div>
              <div className="user-info">
                <span className="user-name">{user.name}</span>
                <span className="user-email">{user.email}</span>
              </div>
              <KeyboardArrowDownIcon
                className={`expand-icon ${userMenuExpanded ? "expanded" : ""}`}
              />
            </div>

            {userMenuExpanded && (
              <div className="user-menu-dropdown">
                <Link
                  className="user-menu-item"
                  to="/profile"
                  onClick={() => handleUserMenuItemClick()}
                >
                  <EditIcon className="user-menu-icon" />
                  <span>{t("profile") || "الملف الشخصي"}</span>
                </Link>
                <Link
                  className="user-menu-item"
                  to="/address"
                  onClick={() => handleUserMenuItemClick()}
                >
                  <AddressIcon className="user-menu-icon" />
                  <span>{t("address") || "العنوان"}</span>
                </Link>
                <button
                  className="user-menu-item logout-item"
                  onClick={() => handleUserMenuItemClick(handleLogout)}
                >
                  <LogoutIcon className="user-menu-icon logout-icon" />
                  <span>{t("logout") || "تسجيل الخروج"}</span>
                </button>
              </div>
            )}
          </div>
        )}

        <ul className="navbar__mobile-nav" ref={menuRef}>
          <li
            className={`menu-item ${activeItem === "home" ? "active" : ""}`}
            style={getMenuItemDelay(isAuthenticated ? 1 : 0)}
          >
            <Link to="/" onClick={() => handleNavClick("home")}>
              {t("home") || "الرئيسية"}
              <KeyboardArrowRightIcon className="nav-arrow" />
            </Link>
          </li>
          {/* Only show services link if singleBooking is not false */}
          {singleBooking !== false && (
            <li
              className={`menu-item ${
                activeItem === "services" ? "active" : ""
              }`}
              style={getMenuItemDelay(isAuthenticated ? 2 : 1)}
            >
              <Link to="/services" onClick={() => handleNavClick("services")}>
                {t("services") || "اطلب زيارة"}
                <KeyboardArrowRightIcon className="nav-arrow" />
              </Link>
            </li>
          )}
          <li
            className={`menu-item ${activeItem === "packages" ? "active" : ""}`}
            style={getMenuItemDelay(isAuthenticated ? 3 : 2)}
          >
            <Link to="/packages" onClick={() => handleNavClick("packages")}>
              {t("packages") || "الاشتراكات"}
              <KeyboardArrowRightIcon className="nav-arrow" />
            </Link>
          </li>
          {/* Show subscriptions, orders, and notifications as main nav items if authenticated */}
          {isAuthenticated && (
            <>
              <li
                className={`menu-item ${
                  activeItem === "subscriptions" ? "active" : ""
                }`}
                style={getMenuItemDelay(4)}
              >
                <Link
                  to="/subscriptions"
                  onClick={() => handleNavClick("subscriptions")}
                >
                  {t("subscriptions") || "اشتراكاتي"}
                  <KeyboardArrowRightIcon className="nav-arrow" />
                </Link>
              </li>
              <li
                className={`menu-item ${
                  activeItem === "orders" ? "active" : ""
                }`}
                style={getMenuItemDelay(5)}
              >
                <Link to="/orders" onClick={() => handleNavClick("orders")}>
                  {t("orders") || "الطلبات"}
                  <KeyboardArrowRightIcon className="nav-arrow" />
                </Link>
              </li>
              <li
                className={`menu-item ${
                  activeItem === "notifications" ? "active" : ""
                }`}
                style={getMenuItemDelay(6)}
              >
                <Link
                  to="/notifications"
                  onClick={() => handleNavClick("notifications")}
                >
                  {t("notifications") || "الاشعارات"}
                  <KeyboardArrowRightIcon className="nav-arrow" />
                </Link>
              </li>
            </>
          )}
          <li
            className={`menu-item ${activeItem === "contact" ? "active" : ""}`}
            style={getMenuItemDelay(isAuthenticated ? 7 : 3)}
          >
            <Link to="/contact" onClick={() => handleNavClick("contact")}>
              {t("contact") || "تواصل معنا"}{" "}
              <KeyboardArrowRightIcon className="nav-arrow" />
            </Link>
          </li>
        </ul>

        {/* Login button only for non-authenticated users */}
        {!isAuthenticated && (
          <div className="navbar__mobile-actions" style={getMenuItemDelay(5)}>
            <GreenButton
              text={t("createAccount")}
              size={isMobile ? "full" : "medium"}
              variant="fill"
              onClick={() => {
                if (onLoginClick) onLoginClick("register"); // Pass "register"
                toggleMenu();
              }}
            />
            <GreenButton
              text={t("login")}
              size={isMobile ? "full" : "medium"}
              variant="outlined"
              onClick={() => {
                if (onLoginClick) onLoginClick("login");
                toggleMenu();
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileMenu;
